import { InScopeNonSection136Detentions, MhaStatus, } from "../types/patientState/PatientState.js";
import { isActiveLatest } from "./mhaStatusHierarchy.js";
/**
 * The two active states that make a patient dual-status at `asOf`, or `null`
 * when the patient is not in dual status. Returns the full latest state on each
 * side, so callers can read its trust and event context, not just the
 * `mhaStatus`.
 *
 * Caller is responsible for passing in the patient's latest states across
 * the merge tree (e.g. via `getLatestPatientStates`) — this does not query.
 */
export function getActiveDualStatusStates(states, asOf) {
    const active = isActiveLatest(asOf);
    const section136 = states.find((s) => active(s) && s.state.mhaStatus.status === MhaStatus.Section136);
    const otherDetention = states.find((s) => active(s) &&
        InScopeNonSection136Detentions.includes(s.state.mhaStatus.status));
    if (!section136 || !otherDetention)
        return null;
    return { section136, otherDetention };
}
/**
 * As `getActiveDualStatusStates`, but returns just the `mhaStatus` of each side
 * — for callers that only report the statuses and need no further context.
 */
export function getActiveDualStatuses(states, asOf) {
    const rows = getActiveDualStatusStates(states, asOf);
    if (!rows)
        return null;
    return {
        section136: rows.section136.state.mhaStatus,
        otherDetention: rows.otherDetention.state.mhaStatus,
    };
}
/**
 * Detects the "coexisting status cleared" transition: the patient was in dual
 * status at `before` but only a single detention remains at `after`. Returns the
 * surviving detention's status, or `null` when this is not that transition.
 *
 * Returns `null` when the whole dual instead collapsed to no active detention
 * (e.g. a discharge to Not Detained) — there is no coexisting status left to
 * report, and the ordinary "status updated to…" log already describes it. Also
 * returns `null` when the surviving status is a wholly new detention (not one of
 * the two that made up the dual), which is likewise an ordinary status change.
 *
 * Caller is responsible for passing in the patient's latest states across the
 * merge tree (e.g. via `getLatestPatientStates`) — this does not query.
 */
export function getClearedCoexistingStatus(statesBefore, statesAfter, asOf) {
    const dualBefore = getActiveDualStatuses(statesBefore, asOf);
    if (!dualBefore)
        return null;
    if (getActiveDualStatuses(statesAfter, asOf))
        return null;
    const active = isActiveLatest(asOf);
    const remainingDetentions = statesAfter.filter((s) => active(s) &&
        (s.state.mhaStatus.status === MhaStatus.Section136 ||
            InScopeNonSection136Detentions.includes(s.state.mhaStatus.status)));
    const remainingDetention = remainingDetentions[0];
    if (remainingDetentions.length !== 1 || remainingDetention === undefined) {
        return null;
    }
    const remainingStatus = remainingDetention.state.mhaStatus.status;
    const wasPartOfDual = remainingStatus === dualBefore.section136.status ||
        remainingStatus === dualBefore.otherDetention.status;
    if (!wasPartOfDual)
        return null;
    return { remainingStatus };
}
// Detention "buckets" at `asOf`. `hasActive*` = a latest row whose detention
// has not yet expired. `hasLatestSection136` = a latest 136 regardless of
// expiry, so that a 136 remains eligible for a status change even once it has
// lapsed — its expiry alone must not exclude it from resolution.
export function getActiveDetentionBuckets(states, asOf) {
    const active = isActiveLatest(asOf);
    const hasLatestSection136 = states.some((s) => s.isLatest && s.state.mhaStatus.status === MhaStatus.Section136);
    const hasActiveSection136 = states.some((s) => active(s) && s.state.mhaStatus.status === MhaStatus.Section136);
    const hasActiveNonSection136Detention = states.some((s) => active(s) &&
        InScopeNonSection136Detentions.includes(s.state.mhaStatus.status));
    return {
        hasActiveSection136,
        hasLatestSection136,
        hasActiveNonSection136Detention,
    };
}
/**
 * The active Section 136 latest row at `asOf`, or `undefined` when the patient
 * has no active 136. "Active" = `isLatest` plus expiry in the future.
 *
 * Caller is responsible for passing in the patient's latest states across the
 * merge tree (e.g. via `getLatestPatientStates`) — this does not query.
 */
export function getActiveSection136(states, asOf) {
    const active = isActiveLatest(asOf);
    return states.find((s) => active(s) && s.state.mhaStatus.status === MhaStatus.Section136);
}
/**
 * The active in-scope non-Section-136 detention latest row at `asOf`, or
 * `undefined` when the patient has none. "Active" = `isLatest` plus expiry in
 * the future. Distinct from an intermediary remnant (an `IntermediaryStatuses`
 * value) that may sit `isLatest` for an ended or lapsed detention, so callers
 * pick the real detention rather than such a remnant.
 *
 * Caller is responsible for passing in the patient's latest states across the
 * merge tree (e.g. via `getLatestPatientStates`) — this does not query.
 */
export function getActiveNonSection136Detention(states, asOf) {
    const active = isActiveLatest(asOf);
    return states.find((s) => active(s) &&
        InScopeNonSection136Detentions.includes(s.state.mhaStatus.status));
}
/**
 * True when, at `asOf`, the patient has both:
 *   - an active Section 136 latest row, and
 *   - an active non-Section-136 detention latest row.
 *
 * "Active" = `isLatest`, plus expiry in the future. Represents a genuine dual
 * status — two coexisting active detentions, one of them a Section 136 — i.e.
 * the condition under which the two statuses must be reconciled rather than one
 * treated as the single truth.
 *
 * Caller is responsible for passing in the patient's latest states
 * across the merge tree (e.g. via `getLatestPatientStates`) — the
 * function does not query.
 */
export function isInDualStatus(states, asOf) {
    return getActiveDualStatuses(states, asOf) !== null;
}
/**
 * Whether a row belongs to the Section 136 dedup "bucket". A patient may carry
 * one latest row per bucket (Section 136 vs non-136), so this classifies a row
 * by the detention it concerns rather than by the status label it currently
 * shows: a Section 136, and a Section 136 that has since been ended by an
 * extension, the outcome form, or an automatic expiry, all belong to the s136
 * bucket even though the ended row's status is a terminal placeholder
 * (NotDetained / Incomplete / Expired) rather than `Section136`. Classifying by
 * label alone would mis-file an ended 136 into the non-136 bucket.
 *
 * This must stay in step with the SQL predicate `isS136BucketSql` in
 * `PatientStateRepository`, which classifies the same rows in raw SQL. Being a
 * SQL string it cannot call this helper (unlike the repository's TypeScript,
 * which does) — change both together.
 */
export function isInSection136Bucket(row) {
    if (row.state.mhaStatus.status === MhaStatus.Section136) {
        return true;
    }
    if (row.event?.type === "s136Extension" ||
        row.event?.type === "s136Outcome") {
        return true;
    }
    return (row.event?.type === "automaticExpiry" &&
        row.event.previousState.mhaStatus.status === MhaStatus.Section136);
}
//# sourceMappingURL=dualStatus.js.map